import { useEffect, useRef } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';

/**
 * Synchronizacja motywu z kontem admina (nie renderuje nic).
 *
 * - ODCZYT: po zalogowaniu przyjmuje motyw zapisany na koncie (serwer wygrywa nad
 *   lokalnym cache w localStorage), raz per admin.
 * - ZAPIS: zmiana motywu przez usera (przełącznik w topbarze) leci do
 *   `PATCH /api/admin/me` (best-effort — localStorage i tak trzyma wybór lokalnie).
 *
 * `settled` chroni przed pętlą: dopóki stan konta nie „dogoni" wartości serwera
 * po hydratacji, nie zapisujemy (inaczej przejściowy render z wartością z
 * localStorage nadpisałby serwer).
 */
export function AdminAppearanceSync() {
  const { admin } = useAuth();
  const { preset, theme, setPreset, setTheme } = useTheme();

  const hydratedFor = useRef<string | null>(null);
  const serverState = useRef<{ preset: string; mode: string } | null>(null);
  const settled = useRef(false);

  // ODCZYT — po zalogowaniu (raz per admin.id).
  useEffect(() => {
    if (!admin) {
      hydratedFor.current = null;
      serverState.current = null;
      settled.current = false;
      return;
    }
    if (hydratedFor.current === admin.id) return;
    hydratedFor.current = admin.id;
    serverState.current = { preset: admin.uiThemePreset, mode: admin.uiThemeMode };
    settled.current = false;
    setPreset(admin.uiThemePreset);
    setTheme(admin.uiThemeMode);
  }, [admin, setPreset, setTheme]);

  // ZAPIS — zmiana przez usera po ustabilizowaniu się stanu na wartości z konta.
  useEffect(() => {
    if (!admin || hydratedFor.current !== admin.id || !serverState.current) return;
    const server = serverState.current;
    const matchesServer = preset === server.preset && theme === server.mode;
    if (!settled.current) {
      if (matchesServer) settled.current = true; // stan dogonił serwer — od teraz zapisujemy zmiany
      return;
    }
    if (matchesServer) return;
    serverState.current = { preset, mode: theme };
    api.patch('/admin/me', { uiThemePreset: preset, uiThemeMode: theme }).catch(() => {
      // best-effort; wybór i tak zapisany lokalnie w ThemeContext (localStorage)
    });
  }, [admin, preset, theme]);

  return null;
}
